import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withRouterConfig,
  withViewTransitions,
} from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { provideTanStackQuery, QueryClient } from '@tanstack/angular-query-experimental';

import { environment } from '@env/environment';
import { APP_ROUTES } from './app.routes';
import { MockInterceptor } from './core/interceptors/mock.interceptor';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { TenantInterceptor } from './core/interceptors/tenant.interceptor';
import { ErrorInterceptor } from './core/interceptors/error.interceptor';
import { RetryInterceptor } from './core/interceptors/retry.interceptor';
import { ThrottleInterceptor } from './core/interceptors/throttle.interceptor';
import { HeadersInterceptor } from './core/interceptors/headers.interceptor';
import { IdempotencyInterceptor } from './core/interceptors/idempotency.interceptor';
import { SocketService } from './core/realtime/socket.service';
import { GlobalErrorHandler } from './core/global-error.handler';
import { I18nService } from './core/i18n/i18n.service';
import { stripTenantRedirectParam } from './core/tenant/tenant-redirect';
import { AuthService } from './core/auth/auth.service';
import { NavigationStore } from './core/navigation/navigation.store';

// Base URL sin el prefijo /api (para health endpoints)
const healthBase = environment.apiUrl;

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      APP_ROUTES,
      withComponentInputBinding(),
      withViewTransitions(),
      withInMemoryScrolling({ scrollPositionRestoration: 'top', anchorScrolling: 'enabled' }),
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
    ),
    provideAnimations(),
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    // Orden de interceptors: el PRIMERO procesa request antes y response después.
    // Mock → Headers → Auth → Tenant → Idempotency → Retry → Error → Throttle
    // MockInterceptor siempre registrado: internamente solo intercepta tracking
    // en cualquier entorno, y todo lo demás solo si useMocks=true.
    { provide: HTTP_INTERCEPTORS, useClass: MockInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: HeadersInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TenantInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: IdempotencyInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: RetryInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ThrottleInterceptor, multi: true },
    // TanStack Query — caché de server state para features que usan injectQuery.
    provideTanStackQuery(new QueryClient()),
    // PrimeNG services globales (toasts, confirms, diálogos dinámicos).
    MessageService,
    ConfirmationService,
    DialogService,
    // Conexiones WebSocket compartidas en toda la app.
    SocketService,
    // Captura errores globales (sin pantalla en blanco).
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    // Pre-carga el bundle i18n antes de mostrar la app.
    provideAppInitializer(() => inject(I18nService).load()),
    // Restaura la sesión desde la cookie httpOnly de refresh al recargar la página.
    // El access token vive solo en memoria y se pierde en cada F5; sin esto el
    // authGuard mandaba a /login en cada refresh. Silencioso: si no hay sesión
    // válida (sin cookie), el guard llevará a /login como siempre. En mocks no
    // aplica (no hay cookie real; se conserva la pantalla de login para dev).
    provideAppInitializer(async () => {
      if (environment.useMocks) return;
      const auth = inject(AuthService);
      const nav = inject(NavigationStore);
      try {
        await firstValueFrom(auth.refresh());
        // Rehidratación en frío: tras recuperar el par de tokens, cargar la identidad
        // para que los guards ya vean el rol al primer navigate (AUTH-identidad §5).
        await firstValueFrom(auth.fetchMe());
        // La DECISIÓN de reubicar vive en `tenantSubdomainGuard`, sobre la ruta
        // `/app`: este initializer corre una sola vez y no cubre el login, que
        // es una navegación del lado del cliente. Acá sólo se limpia el marcador
        // anti-bucle, que es lo propio del arranque: llega en la URL justo
        // después de un salto entre orígenes y no debe quedarse en la barra.
        const cleaned = stripTenantRedirectParam(window.location.href);
        if (cleaned) window.history.replaceState({}, '', cleaned);

        // M21: y la navegación, que ahora gobierna menú y guards de ruta.
        await nav.ensureLoaded();
      } catch {
        /* sin sesión previa (o /me falló) — se continúa; el rol cae al claim del JWT */
      }
    }),
    // TODO: quitar cuando se confirme que el backend responde en todos los ambientes.
    provideAppInitializer(async () => {
      const http = inject(HttpClient);
      const [live, ready] = await Promise.allSettled([
        firstValueFrom(http.get(`${healthBase}/health/live`)),
        firstValueFrom(http.get(`${healthBase}/health/ready`)),
      ]);
      console.group(`[FUGA Health] ${healthBase}`);
      console.log('health/live  :', live.status === 'fulfilled' ? 'OK' : live.reason);
      console.log('health/ready :', ready.status === 'fulfilled' ? 'OK' : ready.reason);
      console.groupEnd();
    }),
  ],
};
